import React, { createContext, useContext, type FunctionComponent, useState } from 'react'
import {
  type ShiftsStateContextType,
  type ShiftsStateType,
} from 'domains/shifts/components/contexts/ShiftsStateContext.type'
import { ShiftType, type ShopperWithShifts } from 'domains/shifts/components/types'
import { type OpenShiftsModalArgs } from 'domains/shifts/components/recurring-shifts/recurringShift.types'

const initialState: ShiftsStateType = {
  isOpenedByDragging: false,
  selectedRecommendationShiftRange: null,
  shiftType: ShiftType.Picking,
  isWeeklyView: false,
  weeklyStaffingData: {},
  isWeekLoading: false,
  isEditMode: false,
  isRecommendationMode: false,
  isShiftsModalOpen: false,
  shiftsFormData: null,
  shiftIdBeingEdited: null,
  recurringShiftIdBeingEdited: null,
  shopperIdBeingEdited: null,
  modalStyles: null,
  onShiftsModalClose: null,
  recommendationType: null,
}

export const ShiftsStateContext = createContext<ShiftsStateContextType>({
  shiftsState: initialState,
  setShiftsState: null,
  updateSelectedRecommendationShiftRange: null,
  handleChangeActiveShiftType: null,
})

export const ShiftsStateContextProvider: FunctionComponent<{}> = ({ children }) => {
  const [shiftsState, setShiftsState] = useState<ShiftsStateType>(initialState)

  const updateSelectedRecommendationShiftRange = (target: 'start' | 'end', value: number) => {
    if (target === 'start') {
      setShiftsState(prevState => ({
        ...prevState,
        selectedRecommendationShiftRange: {
          ...shiftsState.selectedRecommendationShiftRange,
          start: value,
        },
      }))
    } else {
      setShiftsState(prevState => ({
        ...prevState,
        selectedRecommendationShiftRange: {
          ...shiftsState.selectedRecommendationShiftRange,
          end: value,
        },
      }))
    }
  }

  const handleChangeActiveShiftType = (shiftType: ShiftType) => {
    setShiftsState(prevState => ({ ...prevState, shiftType }))
  }

  const openShiftsModal = ({
    formData,
    shiftId,
    shopperId,
    recurringShiftId,
    modalStyles,
    isRecommendationMode,
    recommendationType,
    onClose,
    isEditMode,
  }: OpenShiftsModalArgs) => {
    setShiftsState(prevState => ({
      ...prevState,
      isRecommendationMode,
      recommendationType,
      isEditMode,
      isShiftsModalOpen: true,
      shiftsFormData: formData,
      shiftIdBeingEdited: shiftId,
      recurringShiftIdBeingEdited: recurringShiftId,
      shopperIdBeingEdited: shopperId,
      modalStyles,
      onShiftsModalClose: onClose,
    }))
  }

  const closeShiftsModal = (shoppersState: ShopperWithShifts[]) => {
    setShiftsState(prevState => {
      // remove the placeholder shift upon modal close
      const shoppers = [...(shoppersState || [])]
      shoppers.forEach((shopper, idx) => {
        shoppers[idx].shifts = shopper.shifts.filter(shift => !shift.placeholder)
      })
      return {
        ...prevState,
        isShiftsModalOpen: false,
        shiftsFormData: null,
        shiftIdBeingEdited: null,
        recurringShiftIdBeingEdited: null,
        shopperIdBeingEdited: null,
        modalStyles: null,
        shoppers,
        recommendationType: null,
      }
    })
    if (shiftsState.onShiftsModalClose) {
      shiftsState.onShiftsModalClose()
    }
  }

  return (
    <ShiftsStateContext.Provider
      value={{
        shiftsState,
        setShiftsState,
        updateSelectedRecommendationShiftRange,
        handleChangeActiveShiftType,
        openShiftsModal,
        closeShiftsModal,
      }}
    >
      {children}
    </ShiftsStateContext.Provider>
  )
}

export const useShiftsStateContext = () => useContext(ShiftsStateContext)
